import { Component, type ErrorInfo, type ReactNode } from "react";
import { apiCall } from "../lib/api";
import { isStaleChunkOrImportFailure, tryReloadStaleBuild } from "../lib/staleChunkRecovery";

type Props = { children: ReactNode };
type State = { hasError: boolean; message: string | null };

/**
 * Top-level React error boundary. Catches render-time errors anywhere in the
 * SPA, reports them to the server's `/api/telemetry` endpoint (which writes
 * into the existing `ErrorLog` table), and renders a friendly fallback so the
 * customer can refresh out of a bad state.
 *
 * Async errors (fetch failures, promise rejections inside event handlers) need
 * to be funneled here by their owners; the boundary only catches things React
 * throws during render. The `api` wrapper surfaces fetch errors as typed
 * exceptions; screens that handle them are responsible for showing toasts and
 * not re-throwing them up here.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(err: unknown): State {
    const message = err instanceof Error ? err.message : "Unknown error";
    return { hasError: true, message };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isStaleChunkOrImportFailure(error)) {
      tryReloadStaleBuild();
      return;
    }

    // Best-effort report. We swallow telemetry failures so a flaky network can't
    // mask the original error from the user.
    apiCall("/api/telemetry/errors", {
      method: "POST",
      body: {
        source: "customer-app",
        message: error.message,
        stack: error.stack ?? null,
        componentStack: info.componentStack ?? null,
        url: typeof window !== "undefined" ? window.location.href : null
      }
    }).catch(() => {
      // intentional: no double-error path
    });
  }

  reset = () => {
    this.setState({ hasError: false, message: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="fg fg-error-fallback">
        <div className="fg-error-fallback__card">
          <span className="fg-eyebrow">Fueling Plans · error</span>
          <h1 className="fg-h3">Something went wrong</h1>
          <p className="fg-body-md">
            We hit a problem rendering this page. Try again — if it keeps happening, please
            contact support and we'll dig in.
          </p>
          {/* Raw error text is dev-only — production users see only the
              friendly copy above. Telemetry already captured the full error
              and component stack via componentDidCatch. */}
          {import.meta.env.DEV && this.state.message && (
            <pre className="fg-error-fallback__detail">{this.state.message}</pre>
          )}
          <div className="fg-error-fallback__actions">
            <button
              type="button"
              className="fg-btn fg-btn--primary"
              onClick={() => {
                this.reset();
                if (typeof window !== "undefined") window.location.reload();
              }}>
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
